import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { FetchDataRequest, FetchDataResult } from '../../../../Common/models/grid';
import HeiGrid from '../../../../Common/components/HeiGrid/HeiGrid';
import HeiCheckbox from '../../../../Common/components/Form/HeiCheckbox/HeiCheckbox';
import { CustomGroupUsersFormType } from '../../hooks/useCustomGroupUsersForm';
import { EntityState } from '../../../../Common/api/models/EntityState';

interface CustomGroupUsersTableProps {
  form: UseFormReturn<CustomGroupUsersFormType>;
  fetchUsers: (
    projectId: string,
    toolId: string, 
    groupName: string,
    request: FetchDataRequest
  ) => Promise<FetchDataResult | null>;
  projectId: string;
  toolId: string;
  groupName: string;
  initialUsersRef: React.MutableRefObject<any[]>;
  setInitialUsers: (users: any[]) => void;
  refetchRef?: React.MutableRefObject<(() => void) | undefined>;
}

const CustomGroupUsersTable: React.FC<CustomGroupUsersTableProps> = ({
  form,
  fetchUsers,
  projectId,
  toolId,
  groupName,
  initialUsersRef,
  setInitialUsers,
  refetchRef,
}) => {
  const { getValues, watch } = form;

  const applyInitialFetchResult = useCallback((rows: any[]) => {
    const usersWithEditStatus = rows.map(u => ({
      ...u,
      editStatus: EntityState.NotChanged
    }));
    setInitialUsers(usersWithEditStatus);
    initialUsersRef.current = usersWithEditStatus;
    form.reset({
      users: usersWithEditStatus,
    });
  }, [form, initialUsersRef, setInitialUsers]);

  const mergeFetchedUsersWithForm = useCallback((rows: any[]) => {
    const formUsers = getValues("users");
    const updatedRows = rows.map(apiUser => {
      const formUser = formUsers.find(fu => fu.memberId === apiUser.memberId);
      if (formUser) {
        return {
          ...apiUser,
          isInCustomGroup: formUser.isInCustomGroup,
          editStatus: formUser.editStatus || EntityState.NotChanged
        };
      }
      return {
        ...apiUser,
        editStatus: EntityState.NotChanged
      };
    });

    const updatedUsers = updatedRows.filter(
      row => !formUsers.some(fu => fu.memberId === row.memberId)
    );

    if (updatedUsers.length > 0) {
      form.setValue("users", [...formUsers, ...updatedUsers]);
      const updatedBaseline = [...initialUsersRef.current, ...updatedUsers];
      initialUsersRef.current = updatedBaseline;
      setInitialUsers(updatedBaseline);
    }

    return updatedRows;
  }, [getValues, form, initialUsersRef, setInitialUsers]);

  const handleFetchData = useCallback(async (request: FetchDataRequest): Promise<{ rows: any[]; totalRows: number; }> => {
    try {
      const result = await fetchUsers(projectId, toolId, groupName, request);

      if (!result || !result.rows) {
        return { rows: [], totalRows: 0 };
      }

      if (initialUsersRef.current.length === 0) {
        applyInitialFetchResult(result.rows);
        return { rows: result.rows, totalRows: result.totalRows };
      }

      const updatedRows = mergeFetchedUsersWithForm(result.rows);
      return { rows: updatedRows, totalRows: result.totalRows };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { rows: [], totalRows: 0 };
    }
  }, [fetchUsers, projectId, toolId, groupName, initialUsersRef, applyInitialFetchResult, mergeFetchedUsersWithForm]);

  const userColumns = [
    { field: 'name', headerName: 'Name', width: 300, disableColumnMenu: true },
    { field: 'adPrinciple', headerName: 'AD Principle', width: 400, disableColumnMenu: true },
    { field: 'email', headerName: 'Email', width: 300, disableColumnMenu: true },
    { 
      field: 'isInCustomGroup', 
      headerName: 'In Group', 
      width: 100,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const formUsers = getValues("users");
        const userIndex = formUsers.findIndex(u => u.memberId === params.row.memberId);
        watch(`users.${userIndex}.isInCustomGroup`);
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            '& > div': { marginTop: '0 !important' }
          }}>
            <HeiCheckbox
              control={form.control}
              name={`users.${userIndex}.isInCustomGroup`}
              label=""
            />
          </Box>
        );
      }
    },
  ];

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <HeiGrid
        fetchGridData={handleFetchData}
        columns={userColumns}
        storageSettingsKey={`CustomGroupUsers_${groupName}`}
        searchPlaceholder="Search users by name, email or AD principle"
        getRowId={(row) => row.memberId}
        refFunction={refetchRef}
      />
    </Box>
  );
};

export default CustomGroupUsersTable;